import { FlexBox } from '@ardoq/layout';
import {
  EmptyState,
  EmptyStateNoResultIllustration,
  EmptyStateToAddIllustration,
} from '@ardoq/status-ui';
import { PrimaryButton } from '@ardoq/button';
import { AddIcon } from '@ardoq/icons';
import { type I18nLocale, t, type WithI18nLocale } from '@ardoq/i18n';
import React from 'react';
import { InventoryDatasourceSelection } from './types';
import { APISurveyAttributes } from '@ardoq/api-types';
import { CreateComponentFromSurveyButton } from './interactiveRow/CreateComponentFromSurveyButton';

type DirectCreateEmptyStateProps = {
  onClick: VoidFunction;
  i18nLocale: I18nLocale;
};

const DirectCreateEmptyState = ({
  onClick,
  i18nLocale,
}: DirectCreateEmptyStateProps) => (
  <FlexBox style={{ pointerEvents: 'auto' }}>
    <EmptyState
      title={t(
        i18nLocale,
        'inventory:emptyState.noComponentsTitle',
        'No components here'
      )}
      media={<EmptyStateToAddIllustration />}
    >
      <PrimaryButton onClick={onClick}>
        <AddIcon />
        {t(
          i18nLocale,
          'inventory:emptyState.createComponents',
          'Create components'
        )}
      </PrimaryButton>
    </EmptyState>
  </FlexBox>
);

type SurveyCreateEmptyStateProps = {
  openSurvey: (surveyId: string) => void;
  relevantSurveys: APISurveyAttributes[];
  workspaceNameBySurveyId: Record<string, string>;
  i18nLocale: I18nLocale;
};

const SurveyCreateEmptyState = ({
  openSurvey,
  relevantSurveys,
  workspaceNameBySurveyId,
  i18nLocale,
}: SurveyCreateEmptyStateProps) => (
  <FlexBox style={{ pointerEvents: 'auto' }}>
    <EmptyState
      title={t(
        i18nLocale,
        'inventory:emptyState.noComponentsTitle',
        'No components here'
      )}
      media={<EmptyStateToAddIllustration />}
    >
      <CreateComponentFromSurveyButton
        openSurvey={openSurvey}
        surveys={relevantSurveys}
        workspaceNameBySurveyId={workspaceNameBySurveyId}
        i18nLocale={i18nLocale}
      />
    </EmptyState>
  </FlexBox>
);

type EmptyStateWithLocaleProps = WithI18nLocale;

const NoResultsForFilterEmptyState = ({
  i18nLocale,
}: EmptyStateWithLocaleProps) => (
  <FlexBox style={{ pointerEvents: 'auto' }}>
    <EmptyState
      title={t(
        i18nLocale,
        'inventory:emptyState.noResultsTitle',
        'No results found'
      )}
      media={<EmptyStateNoResultIllustration />}
      description={t(
        i18nLocale,
        'inventory:emptyState.noResultsForFilterDescription',
        'Try different keywords, checking for typos, or adjusting filters to refine your search.'
      )}
    />
  </FlexBox>
);

type NoComponentsEmptyStateProps = {
  description?: string;
  i18nLocale: I18nLocale;
};

const NoComponentsEmptyState = ({
  description,
  i18nLocale,
}: NoComponentsEmptyStateProps) => (
  <FlexBox style={{ pointerEvents: 'auto' }}>
    <EmptyState
      title={t(
        i18nLocale,
        'inventory:emptyState.noComponentsTitle',
        'No components here'
      )}
      media={<EmptyStateToAddIllustration />}
      description={description}
    />
  </FlexBox>
);

const NoResultsForReportDataEmptyState = ({
  i18nLocale,
}: EmptyStateWithLocaleProps) => (
  <FlexBox style={{ pointerEvents: 'auto' }}>
    <EmptyState
      title={t(
        i18nLocale,
        'inventory:emptyState.noComponentsTitle',
        'No components here'
      )}
      media={<EmptyStateNoResultIllustration />}
      description={t(
        i18nLocale,
        'inventory:emptyState.noResultsForReportDataDescription',
        'Try different keywords, or checking for typos to refine your search.'
      )}
    />
  </FlexBox>
);

export type NoRowsOverlayCreateActions = {
  enterCreateComponentsMode: VoidFunction;
  canCreateComponent: boolean;
  shouldDisplayCreateComponentsThroughSurveysButton: boolean;
  relevantSurveys: APISurveyAttributes[];
  workspaceNameBySurveyId: Record<string, string>;
  openSurvey: (surveyId: string) => void;
};

export type NoRowsOverlayComponentProps = {
  datasourceSelection: InventoryDatasourceSelection;
  isAnyFilterPresent: boolean;
  createActions: NoRowsOverlayCreateActions | null;
  i18nLocale: I18nLocale;
};

type CreateComponentsEmptyStateProps = {
  createActions: NoRowsOverlayCreateActions;
  i18nLocale: I18nLocale;
};

const CreateComponentsEmptyState = ({
  createActions: {
    canCreateComponent,
    enterCreateComponentsMode,
    shouldDisplayCreateComponentsThroughSurveysButton,
    relevantSurveys,
    workspaceNameBySurveyId,
    openSurvey,
  },
  i18nLocale,
}: CreateComponentsEmptyStateProps) => {
  if (canCreateComponent) {
    return (
      <DirectCreateEmptyState
        onClick={enterCreateComponentsMode}
        i18nLocale={i18nLocale}
      />
    );
  }

  if (shouldDisplayCreateComponentsThroughSurveysButton) {
    return (
      <SurveyCreateEmptyState
        openSurvey={openSurvey}
        relevantSurveys={relevantSurveys}
        workspaceNameBySurveyId={workspaceNameBySurveyId}
        i18nLocale={i18nLocale}
      />
    );
  }

  return (
    <NoComponentsEmptyState
      description={t(
        i18nLocale,
        'inventory:emptyState.noPermissionDescription',
        "You don't have permission to create components in this workspace."
      )}
      i18nLocale={i18nLocale}
    />
  );
};

const NoRowsOverlayComponent = ({
  datasourceSelection,
  isAnyFilterPresent,
  createActions,
  i18nLocale,
}: NoRowsOverlayComponentProps) => {
  /* Pointer events are for some reason disabled on overlays */
  if (isAnyFilterPresent) {
    return <NoResultsForFilterEmptyState i18nLocale={i18nLocale} />;
  }

  if (datasourceSelection.dataSourceType === 'report') {
    return <NoResultsForReportDataEmptyState i18nLocale={i18nLocale} />;
  }

  if (!createActions) {
    return <NoComponentsEmptyState i18nLocale={i18nLocale} />;
  }

  return (
    <CreateComponentsEmptyState
      createActions={createActions}
      i18nLocale={i18nLocale}
    />
  );
};

export { NoRowsOverlayComponent };
